import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Loader2, X, Inbox, Search, MailOpen, MousePointerClick, UserMinus,
  AlertTriangle, Mail, Link as LinkIcon, ChevronLeft, ChevronRight,
} from 'lucide-react';

const API = '/api'; // Same-origin: /api routes on the serving domain (Preview or Production custom domain) to backend via Emergent ingress. Avoids cross-domain CORS / tracking-prevention failures.

const TAB_CONFIG = [
  { id: 'opened',       label: 'Opened',       icon: MailOpen,         aggKey: 'opened',       color: '#48B64C' },
  { id: 'clicked',      label: 'Clicked',      icon: MousePointerClick, aggKey: 'clicked',      color: '#76BB40' },
  { id: 'unopened',     label: 'Unopened',     icon: Mail,             aggKey: 'unopened',     color: '#888888' },
  { id: 'unsubscribed', label: 'Unsubscribed', icon: UserMinus,        aggKey: 'unsubscribed', color: '#e07070' },
  { id: 'hardbounced',  label: 'Bounced',      icon: AlertTriangle,    aggKey: 'hardbounced',  color: '#d4914a' },
  { id: 'links',        label: 'Link Clicks',  icon: LinkIcon,         aggKey: null,           color: '#5b9bd5' },
];

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso.replace(' ', 'T') + (iso.includes('T') || iso.includes('Z') ? '' : 'Z'));
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return iso; }
}

export default function CampaignDrillDown({ campaign, onClose }) {
  const [tab, setTab] = useState('opened');
  const [recipients, setRecipients] = useState([]);
  const [links, setLinks] = useState([]);
  const [aggregations, setAggregations] = useState({});
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');

  const stats = campaign.stats || {};

  // Load activity for current tab
  useEffect(() => {
    if (tab === 'links') return;
    let cancelled = false;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const res = await axios.get(
          `${API}/mailerlite/campaign-activity/${campaign.campaign_id}?type=${tab}&limit=100&page=${page}&sort=opens_count`
        );
        if (cancelled) return;
        setRecipients(res.data.recipients || []);
        setAggregations(res.data.aggregations || {});
        setTotal(res.data.total || 0);
        setLastPage(res.data.last_page || 1);
        if (res.data.message) setError(res.data.message);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Failed to load activity');
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [tab, page, campaign.campaign_id]);

  // Load links once (independent of tab)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/mailerlite/campaign-links/${campaign.campaign_id}`);
        if (!cancelled) setLinks(res.data.links || []);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [campaign.campaign_id]);

  // Reset page when tab changes
  useEffect(() => { setPage(1); }, [tab]);

  const filtered = useMemo(() => {
    if (!filter.trim()) return recipients;
    const q = filter.toLowerCase();
    return recipients.filter((r) =>
      (r.name || '').toLowerCase().includes(q) ||
      (r.email || '').toLowerCase().includes(q)
    );
  }, [recipients, filter]);

  return (
    <div
      data-testid="campaign-drilldown"
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-3 lg:p-6"
      onClick={onClose}
    >
      <div
        className="bg-hg-surface border border-hg-border rounded-sm w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-hg-border bg-hg-bg/40">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-[10px] font-sans text-hg-green uppercase tracking-[0.2em]">Campaign Details</div>
              <div className="text-base font-sans text-hg-text mt-1 truncate">{campaign.subject || campaign.name}</div>
              <div className="text-[11px] text-hg-text-dim font-sans mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                <span>Sent {fmtDate(campaign.finished_at || campaign.started_at)}</span>
                <span>From {campaign.from_name}</span>
              </div>
            </div>
            <button
              data-testid="drilldown-close-btn"
              onClick={onClose}
              className="text-hg-text-dim hover:text-hg-text p-1 -mr-1"
            ><X size={18} /></button>
          </div>

          {/* Top stat row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
            <MiniStat label="Sent" value={(stats.sent || 0).toLocaleString()} />
            <MiniStat label="Opens" value={(stats.opens_count || 0).toLocaleString()} sub={`${Math.round((stats.open_rate || 0) * 100) / 100}%`} accent="#48B64C" onClick={() => setTab('opened')} />
            <MiniStat label="Clicks" value={(stats.clicks_count || 0).toLocaleString()} sub={`${Math.round((stats.click_rate || 0) * 100) / 100}%`} accent="#76BB40" onClick={() => setTab('clicked')} />
            <MiniStat label="Unsubs" value={(stats.unsubscribes_count || 0).toLocaleString()} accent="#e07070" onClick={() => setTab('unsubscribed')} />
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-hg-border bg-hg-bg/20 overflow-x-auto">
          <div className="flex min-w-max">
            {TAB_CONFIG.map((t) => {
              const Icon = t.icon;
              const count = t.id === 'links'
                ? links.length
                : (aggregations[t.aggKey] ?? 0);
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  data-testid={`drilldown-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-xs font-sans border-b-2 transition-colors whitespace-nowrap ${
                    active
                      ? 'border-hg-green text-hg-text bg-hg-surface'
                      : 'border-transparent text-hg-text-dim hover:text-hg-text hover:bg-hg-surface/40'
                  }`}
                >
                  <Icon size={13} style={{ color: active ? t.color : undefined }} strokeWidth={1.5} />
                  <span>{t.label}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${active ? 'bg-hg-green/20 text-hg-green' : 'bg-hg-border/60 text-hg-text-dim'}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {tab === 'links' ? (
            <LinksTable links={links} />
          ) : (
            <>
              {/* Filter row */}
              <div className="px-5 py-3 border-b border-hg-border flex items-center gap-2">
                <Search size={13} className="text-hg-text-dim" />
                <input
                  data-testid="drilldown-search"
                  type="text"
                  placeholder="Filter by name or email…"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="flex-1 bg-transparent border-0 text-sm font-sans text-hg-text placeholder-hg-text-dim focus:outline-none focus:ring-0"
                />
                <span className="text-[10px] font-sans text-hg-text-dim">{filtered.length} of {total}</span>
              </div>

              {loading ? (
                <div className="flex justify-center py-16">
                  <Loader2 size={20} className="animate-spin text-hg-green" />
                </div>
              ) : filtered.length === 0 ? (
                <EmptyState message={error || `No ${tab} recipients yet.`} />
              ) : (
                <RecipientsTable recipients={filtered} tab={tab} />
              )}

              {/* Pagination */}
              {lastPage > 1 && (
                <div className="px-5 py-3 border-t border-hg-border flex items-center justify-between text-xs font-sans">
                  <span className="text-hg-text-dim">Page {page} of {lastPage}</span>
                  <div className="flex gap-1">
                    <button
                      data-testid="drilldown-prev-btn"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      className="px-2 py-1 border border-hg-border rounded-sm text-hg-text-dim hover:text-hg-text hover:border-hg-green disabled:opacity-30"
                    ><ChevronLeft size={12} /></button>
                    <button
                      data-testid="drilldown-next-btn"
                      onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                      disabled={page >= lastPage}
                      className="px-2 py-1 border border-hg-border rounded-sm text-hg-text-dim hover:text-hg-text hover:border-hg-green disabled:opacity-30"
                    ><ChevronRight size={12} /></button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, sub, accent, onClick }) {
  const cls = `bg-hg-surface border border-hg-border rounded-sm px-3 py-2 text-left w-full${onClick ? ' cursor-pointer hover:border-hg-green transition-colors' : ''}`;
  const inner = (
    <>
      <div className="text-[9px] uppercase tracking-[0.18em] text-hg-text-dim font-sans">{label}</div>
      <div className="font-display text-lg text-hg-text tracking-wider mt-0.5" style={{ color: accent }}>{value}</div>
      {sub && <div className="text-[10px] text-hg-text-dim font-sans">{sub}</div>}
    </>
  );
  return onClick
    ? <button type="button" data-testid={`ministat-${String(label).toLowerCase()}`} onClick={onClick} className={cls}>{inner}</button>
    : <div className={cls}>{inner}</div>;
}

function RecipientsTable({ recipients, tab }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm font-sans" data-testid="drilldown-recipients-table">
        <thead>
          <tr className="text-left text-[10px] text-hg-text-dim uppercase tracking-wider border-b border-hg-border bg-hg-bg/30">
            <th className="px-5 py-2.5 font-normal">Recipient</th>
            <th className="px-3 py-2.5 font-normal text-right">Opens</th>
            <th className="px-3 py-2.5 font-normal text-right hidden sm:table-cell">Clicks</th>
            <th className="px-3 py-2.5 font-normal text-right hidden md:table-cell">All-time Open %</th>
            <th className="px-5 py-2.5 font-normal text-right hidden lg:table-cell">Last Activity</th>
          </tr>
        </thead>
        <tbody>
          {recipients.map((r, i) => (
            <tr
              key={r.email + i}
              data-testid={`drilldown-recipient-${i}`}
              className="border-b border-hg-border/30 hover:bg-hg-bg/40"
            >
              <td className="px-5 py-2.5">
                <div className="text-sm text-hg-text truncate max-w-[18rem]">{r.name || r.email}</div>
                {r.name && <div className="text-[10px] text-hg-text-dim font-sans truncate max-w-[18rem]">{r.email}</div>}
              </td>
              <td className="px-3 py-2.5 text-right text-hg-green">{r.opens_count}</td>
              <td className="px-3 py-2.5 text-right hidden sm:table-cell text-hg-text">{r.clicks_count}</td>
              <td className="px-3 py-2.5 text-right hidden md:table-cell text-hg-text-dim">
                {Math.round((r.subscriber_open_rate || 0) * 10) / 10}%
              </td>
              <td className="px-5 py-2.5 text-right hidden lg:table-cell text-hg-text-dim text-[11px]">
                {fmtDate(r.last_activity_at)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LinksTable({ links }) {
  if (links.length === 0) {
    return <EmptyState message="No tracked links in this campaign." />;
  }
  const max = Math.max(1, ...links.map((l) => l.unique_clicks_count || 0));
  return (
    <div className="p-5 space-y-2" data-testid="drilldown-links">
      {links.map((l) => {
        const pct = ((l.unique_clicks_count || 0) / max) * 100;
        return (
          <div key={l.id} className="bg-hg-bg border border-hg-border rounded-sm p-3" data-testid={`drilldown-link-${l.id}`}>
            <div className="flex items-center justify-between gap-3 mb-2">
              <a
                href={l.url}
                target="_blank"
                rel="noreferrer"
                className="text-sm font-sans text-hg-text hover:text-hg-green truncate max-w-[80%]"
              >{l.label || l.url}</a>
              <div className="flex items-center gap-3 text-[11px] font-sans flex-shrink-0">
                <span className="text-hg-text-dim">{l.clicks_count} total</span>
                <span className="text-hg-green font-medium">{l.unique_clicks_count} unique</span>
              </div>
            </div>
            <div className="h-1 bg-hg-border/40 rounded-sm overflow-hidden">
              <div className="h-full bg-hg-green" style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-2" data-testid="drilldown-empty">
      <Inbox size={28} className="text-hg-text-dim" strokeWidth={1.5} />
      <p className="text-sm text-hg-text-dim font-sans">{message}</p>
    </div>
  );
}
